/**
 * Shared chart.js styling, so every chart on the dashboard reads as one system.
 *
 * Canvas cannot resolve CSS custom properties, so the values are read off the
 * document at call time -- tokens.css stays the single source of truth, and the
 * fallbacks only apply where there is no document (tests).
 *
 * Note: chart.js 2 cannot round bar ends. Bars here are capped in thickness and
 * given breathing room instead; rounded data-ends arrive with the chart library
 * upgrade.
 */

function token(name: string, fallback: string): string {
    if (typeof document === 'undefined') {
        return fallback;
    }
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
}

export const chartColors = {
    series1: () => token('--series-1', '#2a78d6'),
    series2: () => token('--series-2', '#eb6834'),
    domain: (name: string, fallback: string) => token(`--domain-${name}`, fallback),
    track: () => token('--seq-100', '#cde2fb'),
    surface: () => token('--surface-1', '#fcfcfb'),
    gridline: () => token('--gridline', '#e1e0d9'),
    baseline: () => token('--baseline', '#c3c2b7'),
    muted: () => token('--text-muted', '#898781'),
    primaryInk: () => token('--text-primary', '#0b0b0b')
};

const FONT_FAMILY = "system-ui, -apple-system, 'Segoe UI', sans-serif";

function axis(showGrid: boolean) {
    return [{
        gridLines: {
            display: showGrid,
            color: chartColors.gridline(),
            zeroLineColor: chartColors.baseline(),
            drawBorder: false,
            drawTicks: false
        },
        ticks: {
            fontColor: chartColors.muted(),
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            padding: 8,
            beginAtZero: true
        }
    }];
}

function baseOptions() {
    return {
        responsive: true,
        maintainAspectRatio: false,
        // One series per chart here, so the title carries identity and a
        // legend box would only add chrome.
        legend: {display: false},
        tooltips: {
            backgroundColor: chartColors.primaryInk(),
            titleFontFamily: FONT_FAMILY,
            bodyFontFamily: FONT_FAMILY,
            cornerRadius: 6,
            xPadding: 10,
            yPadding: 8,
            displayColors: false
        },
        layout: {padding: {top: 4, right: 4}}
    };
}

/** Trend over time: 2px line, >=8px end markers ringed in the surface color. */
export function lineChart(labels: string[], values: number[], label: string, color?: string) {
    const hue = color ?? chartColors.series1();
    return {
        data: {
            labels,
            datasets: [{
                label,
                data: values,
                borderColor: hue,
                backgroundColor: hue,
                borderWidth: 2,
                lineTension: 0.25,
                fill: false,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: hue,
                pointBorderColor: chartColors.surface(),
                pointBorderWidth: 2
            }]
        },
        options: {
            ...baseOptions(),
            scales: {xAxes: axis(false), yAxes: axis(true)}
        }
    };
}

/** Magnitude comparison. Horizontal when the category names are long. */
export function barChart(labels: string[], values: number[], label: string, horizontal = false, color?: string) {
    const hue = color ?? chartColors.series1();
    return {
        data: {
            labels,
            datasets: [{
                label,
                data: values,
                backgroundColor: hue,
                hoverBackgroundColor: chartColors.primaryInk(),
                maxBarThickness: 24,
                categoryPercentage: 0.7,
                barPercentage: 0.9
            }]
        },
        options: {
            ...baseOptions(),
            scales: horizontal
                ? {xAxes: axis(true), yAxes: axis(false)}
                : {xAxes: axis(false), yAxes: axis(true)}
        }
    };
}
